import FormDateTimePicker from '../../../../components/form/FormDateTimePicker';
import { __, formatDateTime } from '../../../../utils';
import {
  Button,
  Dialog,
  Divider,
  Stack,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';
import { Form } from 'react-final-form';

type Props = {
  open: boolean;
  /** Kedy sa hlasovanie otvorilo. Len sa hlási, meniť sa už nedá. */
  openFrom?: string;
  /** Doterajší termín uzavretia; bez neho sa hlasovanie zatvára ručne. */
  openTo?: string;
  loading?: boolean;
  onCancel: () => void;
  /** `null` znamená „bez termínu" - hlasovanie potom beží, kým ho niekto neuzavrie. */
  onConfirm: (openTo: Date | null) => void;
};

type Values = { openTo: Date | null };

/**
 * Termín uzavretia bežiaceho hlasovania.
 *
 * **Jediná vec, ktorá sa na otvorenom hlasovaní dá zmeniť okrem viditeľnosti.**
 * Obsah (otázky, možnosti, publikum) je od publikovania zamknutý
 * (`VOTING_ALREADY_ACTIVE`) a `open_from` je zamknutý zvlášť - hlasovanie už
 * beží a odovzdané hlasy sa k jeho začiatku vzťahujú. Dátum otvorenia je preto
 * v dialógu **veta, nie pole**; keby bol poľom, sľuboval by zmenu, ktorú server
 * neprijme.
 *
 * Prázdne pole je platná hodnota, nie nevyplnený formulár: krížik v poli
 * ([[FormDateTimePicker]] ho kreslí sám) termín zruší a hlasovanie potom beží,
 * kým ho úradník nezavrie tlačidlom. Preto tu nie je `required` a preto sa
 * hlásenie pod poľom rozdvojuje - inak sa „uložiť bez dátumu" nedá rozoznať od
 * omylom vymazaného poľa.
 */
const VotingScheduleDialog: React.FC<Props> = ({
  open,
  openFrom,
  openTo,
  loading,
  onCancel,
  onConfirm,
}) => {
  /**
   * Stabilná referencia - `react-final-form` porovnáva `initialValues` plytko
   * a pri zmene formulár reinicializuje (detail v `.docs/vyvoj/formulare.md`).
   */
  const initialValues = React.useMemo<Values>(
    () => ({ openTo: openTo ? new Date(openTo) : null }),
    [openTo],
  );

  return (
    <Dialog open={open} onClose={onCancel} maxWidth='xs' fullWidth>
      <Form<Values>
        initialValues={initialValues}
        onSubmit={(values) => onConfirm(values.openTo ?? null)}
      >
        {({ handleSubmit, values }) => (
          <form onSubmit={handleSubmit} noValidate>
            <Stack sx={{ p: 3, pb: 2, gap: 0.75 }}>
              <Typography
                component='b'
                sx={{ fontSize: 19, fontWeight: 700, lineHeight: 1.3 }}
              >
                {__('Termín uzavretia')}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {openFrom
                  ? `${__('Hlasovanie je otvorené od')} ${formatDateTime(new Date(openFrom))}. ${__('Dátum otvorenia sa už zmeniť nedá, koniec áno.')}`
                  : __('Dátum otvorenia sa už zmeniť nedá, koniec áno.')}
              </Typography>
            </Stack>

            <Divider />

            <Stack sx={{ px: 3, py: 2.5, gap: 0.5 }}>
              <FormDateTimePicker
                name='openTo'
                label={__('Uzavrieť')}
                // Termín v minulosti by hlasovanie zavrel; na to je tlačidlo
                // „Uzavrieť hlasovanie", nie zmena termínu. Server to odmieta
                // ako `VOTING_INVALID_DATES`.
                minDate={new Date()}
                validate={(value: Date | null) =>
                  value && value <= new Date()
                    ? __('Termín uzavretia musí byť v budúcnosti.')
                    : undefined
                }
              />
              <Typography variant='caption' color='text.secondary'>
                {values.openTo
                  ? __('V tomto čase sa hlasovanie zavrie samo.')
                  : __(
                      'Bez termínu bude hlasovanie bežať, kým ho neuzavriete tlačidlom.',
                    )}
              </Typography>
            </Stack>

            <Divider />

            <Stack
              direction='row'
              alignItems='center'
              gap={1}
              sx={{ px: 3, py: 2 }}
            >
              <Button variant='text' onClick={onCancel} disabled={loading}>
                {__('Zrušiť')}
              </Button>
              <Button type='submit' loading={loading} sx={{ ml: 'auto' }}>
                {__('Uložiť termín')}
              </Button>
            </Stack>
          </form>
        )}
      </Form>
    </Dialog>
  );
};

export default VotingScheduleDialog;
